import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Empty,
  Select,
  Spin,
  Tabs,
  Typography,
  message,
} from 'antd';
import { motion } from 'framer-motion';
import { listLocalMetaFlows, listMetaFlowLeads } from 'services/metaFlowService';
import { useMyContext } from 'Context/MyContextProvider';
import CreateGroupFromLeadsDrawer from './CreateGroupFromLeadsDrawer';
import { fetchAllFlowLeads } from './flowLeadsGroupUtils';
import {
  FlowLeadsAnalyticsTab,
  FlowLeadsListTab,
  copyLeadAnswers,
  exportLeadsToExcel,
} from './FlowLeadsShared';

const { Title, Text } = Typography;
const { Option } = Select;

const MetaFlowLeads = () => {
  const { Permisson } = useMyContext();
  const canCreateGroup = Permisson?.includes('View Group');
  const [flows, setFlows] = useState([]);
  const [flowFilter, setFlowFilter] = useState('');
  const [flowInfo, setFlowInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [total, setTotal] = useState(0);
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    listLocalMetaFlows()
      .then((data) => setFlows(data?.data || []))
      .catch(() => setFlows([]));
  }, []);

  const fetchLeads = useCallback(async () => {
    if (!flowFilter) {
      return;
    }

    setLoading(true);
    try {
      const params = {
        meta_flow_id: flowFilter,
        page,
        per_page: perPage,
        status: statusFilter,
      };
      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const data = await listMetaFlowLeads(params);
      setRows(data?.data || []);
      setStats(data?.stats || null);
      setFlowInfo(data?.flow || null);
      setTotal(data?.meta?.total || 0);
    } catch (error) {
      console.error('Failed to load flow leads:', error);
      message.error('Failed to load flow leads');
    } finally {
      setLoading(false);
    }
  }, [flowFilter, page, perPage, searchText, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    if (!flowFilter) {
      setChartsReady(false);
      return undefined;
    }

    setChartsReady(false);
    const timer = setTimeout(() => {
      setChartsReady(true);
      window.dispatchEvent(new Event('resize'));
    }, 150);

    return () => clearTimeout(timer);
  }, [flowFilter]);

  const handleFlowChange = (value) => {
    const next = value || '';
    setFlowFilter(next);

    if (!next) {
      setRows([]);
      setStats(null);
      setFlowInfo(null);
      setTotal(0);
      setSearchText('');
      setStatusFilter('all');
      setPage(1);
      return;
    }

    setSearchText('');
    setStatusFilter('all');
    setPage(1);
  };

  const selectedFlowName = flowInfo?.name
    || flows.find((flow) => flow.meta_flow_id === flowFilter)?.name
    || 'Flow';

  const [exportLoading, setExportLoading] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const handleExport = async () => {
    if (!flowFilter) return;
    setExportLoading(true);
    try {
      const params = {
        meta_flow_id: flowFilter,
        page: 1,
        per_page: 100000,
        status: statusFilter,
      };
      if (searchText.trim()) {
        params.search = searchText.trim();
      }
      const data = await listMetaFlowLeads(params);
      const allLeads = data?.data || [];
      exportLeadsToExcel(allLeads, `${selectedFlowName}_leads.xlsx`);
    } catch (error) {
      console.error('Failed to export flow leads:', error);
      message.error('Failed to export leads');
    } finally {
      setExportLoading(false);
    }
  };

  const fetchLeadsForGroup = useCallback(
    () => fetchAllFlowLeads({
      metaFlowId: flowFilter,
      status: statusFilter,
      search: searchText,
    }),
    [flowFilter, searchText, statusFilter],
  );

  const tabItems = [
    {
      key: 'analytics',
      label: 'Analytics',
      children: (
        <FlowLeadsAnalyticsTab stats={stats} chartsReady={chartsReady} loading={loading} />
      ),
    },
    {
      key: 'leads',
      label: 'Leads',
      children: (
        <FlowLeadsListTab
          rows={rows}
          stats={stats}
          loading={loading}
          searchText={searchText}
          onSearchChange={(value) => {
            setSearchText(value);
            setPage(1);
          }}
          statusFilter={statusFilter}
          onStatusFilterChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          page={page}
          perPage={perPage}
          total={total}
          onPaginationChange={(nextPage, nextSize) => {
            setPage(nextPage);
            setPerPage(nextSize);
          }}
          onCopy={copyLeadAnswers}
          emptyDescription="No submissions yet for this flow"
          cardsPerRow={4}
          pageSizeOptions={['8', '16', '24', '32']}
          onExport={handleExport}
          exportLoading={exportLoading}
          onCreateGroup={canCreateGroup && total > 0 ? () => setCreateGroupOpen(true) : undefined}
        />
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        bordered={false}
        style={{ borderRadius: 18, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04)' }}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 20 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Title level={4} style={{ margin: 0 }}>Flow Leads</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Analytics and submissions for a selected flow
            </Text>
          </div>
          <Select
            showSearch
            allowClear
            placeholder="Select a flow"
            value={flowFilter || undefined}
            onChange={handleFlowChange}
            style={{ minWidth: 280 }}
            optionFilterProp="children"
          >
            {flows.map((flow) => (
              <Option key={flow.meta_flow_id} value={flow.meta_flow_id}>
                {flow.name || flow.meta_flow_id}
              </Option>
            ))}
          </Select>
        </div>

        {!flowFilter ? (
          <Empty description="Select a flow to view leads and analytics" style={{ marginTop: 48 }} />
        ) : (
          <Spin spinning={loading}>
            <Tabs defaultActiveKey="analytics" items={tabItems} />
          </Spin>
        )}
      </Card>

      <CreateGroupFromLeadsDrawer
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        fetchLeads={fetchLeadsForGroup}
        sourceLabel={selectedFlowName}
      />
    </motion.div>
  );
};

export default MetaFlowLeads;
